/**
 * Variant Card Sort
 *
 * Keeps variant listing grid sorted client-side when variant display is enabled.
 * Currently supports price sorting (ascending/descending).
 */
(function () {
  'use strict';

  const VARIANTS_PER_PAGE = 9;
  const DEBOUNCE_MS = 50;

  const isVariantListing = () =>
    document.querySelectorAll('.product-grid__item[data-variant-color]').length > 0;

  const getGrid = () => document.querySelector('.product-grid, ul.product-grid');

  const getSortBy = () => {
    const url = new URL(window.location.href);
    return (
      url.searchParams.get('sort_by') ||
      document.documentElement.dataset.currentSortBy ||
      ''
    );
  };

  const getCards = () =>
    Array.from(document.querySelectorAll('.product-grid__item[data-variant-id]')).filter(
      (el) => el instanceof HTMLElement
    );

  const hasActiveColorFilter = () => {
    try {
      const url = new URL(window.location.href);
      for (const [key, value] of url.searchParams.entries()) {
        if (!value) continue;
        const k = String(key).toLowerCase();
        if (k.startsWith('filter.') && (k.includes('color') || k.includes('colour') || k.includes('s_d_variant_color_filter'))) {
          return true;
        }
      }
    } catch {
      // ignore
    }
    return false;
  };

  const applyPaginationAfterSort = (cards) => {
    const filterActive = hasActiveColorFilter();

    cards.forEach((card, idx) => {
      const page = Math.floor(idx / VARIANTS_PER_PAGE) + 1;
      card.dataset.page = String(page);

      const isFilterHidden = card.classList.contains('filter-hidden');

      if (filterActive) {
        // If a filter is active, show all matching cards immediately.
        if (!isFilterHidden) {
          card.classList.remove('product-grid__item--hidden');
          card.classList.add('active');
          card.style.setProperty('display', 'block', 'important');
        } else {
          card.classList.add('product-grid__item--hidden');
          card.classList.remove('active');
          card.style.setProperty('display', 'none', 'important');
        }
        return;
      }

      // No filter: keep the existing scroll reveal process (page 1 visible, others hidden)
      if (page === 1 && !isFilterHidden) {
        card.classList.remove('product-grid__item--hidden');
        card.classList.add('active');
        card.style.setProperty('display', 'block', 'important');
      } else {
        card.classList.add('product-grid__item--hidden');
        card.classList.remove('active');
        card.style.setProperty('display', 'none', 'important');
      }
    });

    // Let the scroll reveal system continue from the new ordering
    try {
      /** @type {any} */
      const globalWindow = window;
      if (globalWindow.VariantCardScrollReveal && typeof globalWindow.VariantCardScrollReveal.init === 'function') {
        setTimeout(() => globalWindow.VariantCardScrollReveal.init(), 0);
      }
    } catch {
      // ignore
    }
  };

  let lastSignature = '';

  const sortCardsIfNeeded = () => {
    if (!isVariantListing()) return;
    const sortBy = getSortBy();
    const isPriceAsc = sortBy === 'price-ascending' || sortBy === 'price';
    const isPriceDesc = sortBy === 'price-descending';
    if (!isPriceAsc && !isPriceDesc) return;

    const grid = getGrid();
    if (!(grid instanceof HTMLElement)) return;

    const cards = getCards();
    if (cards.length === 0) return;

    // Avoid re-sorting the same thing repeatedly (facets/update can fire multiple times)
    const signature = `${sortBy}|${cards.length}|${window.location.search}`;
    if (signature === lastSignature) return;
    lastSignature = signature;

    const sorted = cards.slice().sort((a, b) => {
      let aPrice = parseInt(a.dataset.price || '', 10);
      let bPrice = parseInt(b.dataset.price || '', 10);

      // If some cards are missing price (e.g., skeletons without data), push them to the end for asc
      // and to the beginning for desc. This prevents "skipping" real cards at the top.
      if (!Number.isFinite(aPrice)) aPrice = isPriceAsc ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      if (!Number.isFinite(bPrice)) bPrice = isPriceAsc ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;

      return isPriceAsc ? aPrice - bPrice : bPrice - aPrice;
    });

    const frag = document.createDocumentFragment();
    sorted.forEach((c) => frag.appendChild(c));
    grid.appendChild(frag);

    // Rebuild pagination state so scroll reveal reveals the next cards in sorted order
    applyPaginationAfterSort(sorted);
  };

  let debounceTimer = null;
  const run = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      requestAnimationFrame(() => sortCardsIfNeeded());
    }, DEBOUNCE_MS);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  document.addEventListener('facets:update', run);
  window.addEventListener('popstate', run);
})();




